import { createFileRoute, notFound, redirect } from "@tanstack/react-router"
import * as z from "zod"

import { pick } from "@repo/utils/collections"
import { logger } from "@repo/utils/logger"
import { getExtendedBusinessEntitiesQueryOptions } from "~/modules/business-entities/business-entities.queries"
import {
  getExtendedClientQueryOptions,
  getExtendedClientsQueryOptions,
} from "~/modules/clients/clients.queries"
import { ClientLayout } from "~/modules/clients/components/client-layout"
import { useFeatureConfigs } from "~/modules/system-config/hooks/use-feature-configs"

const log = logger.child({
  module: "clients-route",
})

const searchSchema = z.object({
  be_slug: z.string().optional(),
})

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/clients/$clientSlug",
)({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => pick(search, ["be_slug"]),
  beforeLoad: async ({ location, params, search }) => {
    // Redirect to the overview route if this naked route is accessed directly
    if (location.pathname === `/clients/${params.clientSlug}`) {
      throw redirect({
        to: "./overview",
        from: "/clients/$clientSlug",
        params,
        search,
      })
    }
  },
  loader: async ({ context, params }) => {
    context.queryClient
      .ensureQueryData(getExtendedBusinessEntitiesQueryOptions())
      .catch((error) => {
        log.error("Error getting extended business entities", { error })
        return null
      })
    context.queryClient
      .ensureQueryData(
        getExtendedClientQueryOptions({ clientSlug: params.clientSlug }),
      )
      .catch((error) => {
        log.error("Error getting extended client", { error })
        return null
      })
    // Pre-fetch clients list (without business entity filter for initial load)
    context.queryClient
      .ensureQueryData(
        getExtendedClientsQueryOptions({
          filters: {
            page: 1,
            page_size: 1000,
          },
        }),
      )
      .catch((error) => {
        log.error("Error getting extended clients", { error })
        return null
      })
  },
})

function RouteComponent() {
  const { isClientManagementOn } = useFeatureConfigs()
  const { clientSlug } = Route.useParams()
  const { be_slug } = Route.useSearch()

  if (!isClientManagementOn) {
    throw notFound()
  }

  return <ClientLayout clientSlug={clientSlug} businessEntitySlug={be_slug} />
}
